<script setup>
import { computed } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useNovuStore } from '@/stores/novu.store';
import { useLoaderStore } from '@/stores/loader.store';
import { useFrontegg } from '@frontegg/vue';
import { Permissions } from '@/utils/constants';
import { hasAccess } from '@/utils/utils';
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarMenuSkeleton,
    useSidebar
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { ChartBar, ArrowUpDown, Share2, Sliders, LayoutList, Code, AlertCircle, ChevronRight, CloudUpload, CloudDownload } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';

const route = useRoute();
const authStore = useAuthStore();
const novuStore = useNovuStore();
const loaderStore = useLoaderStore();

const unreadCount = computed(() => novuStore.userUnreadNotificationsCount || 0);

// Check if navigation should show skeleton (during app initialization, tenant switching, or service switching)
const isNavigationLoading = computed(
    () => loaderStore.isLoading('app.init') || loaderStore.isLoading('tenant.switch') || loaderStore.isLoading('services.switch')
);

const navigation = computed(() => {
    const currentPath = route.path;
    const nav = [
        {
            title: 'Dashboard',
            id: 'dashboard',
            route: { name: 'dashboard' },
            icon: ChartBar,
            isActive: currentPath === '/' || currentPath.includes('dashboard')
        },
        {
            title: 'Pipelines',
            id: 'pipelines',
            route: { name: 'pipelines' },
            icon: ArrowUpDown,
            isActive: currentPath.includes('pipelines'),
            accessRules: {
                permissions: {
                    values: [Permissions.READ_PIPELINES, Permissions.READ_PIPELINES_METRICS],
                    type: 'and'
                }
            }
        },
        {
            title: 'Connectors',
            id: 'connectors',
            icon: Share2,
            isActive: currentPath.includes('connectors'),
            items: [
                { title: 'Sources', url: { name: 'sources-list' }, icon: CloudUpload, isActive: currentPath.includes('sources') },
                { title: 'Destinations', url: { name: 'destinations-list' }, icon: CloudDownload, isActive: currentPath.includes('destinations') }
            ],
            accessRules: {
                permissions: {
                    values: [
                        Permissions.READ_SOURCES,
                        Permissions.READ_DESTINATIONS,
                        Permissions.READ_SOURCE_METRICS,
                        Permissions.READ_DESTINATION_METRICS
                    ],
                    type: 'or'
                }
            }
        },
        {
            title: 'Transforms',
            id: 'transforms',
            route: { name: 'transforms' },
            icon: Sliders,
            isActive: currentPath.includes('transforms'),
            accessRules: {
                permissions: {
                    values: [Permissions.READ_TRANSFORMS, Permissions.READ_TRANSFORM_METRICS],
                    type: 'and'
                }
            }
        },
        {
            title: 'Topics',
            id: 'topics',
            route: { name: 'topics' },
            icon: LayoutList,
            isActive: currentPath.includes('topics'),
            accessRules: {
                permissions: {
                    values: [Permissions.READ_TOPICS, Permissions.READ_TOPIC_METRICS],
                    type: 'and'
                }
            }
        },
        {
            title: 'Logs',
            id: 'logs',
            route: { name: 'logs' },
            icon: Code,
            isActive: currentPath.includes('logs'),
            accessRules: {
                permissions: {
                    values: [Permissions.READ_SOURCE_METRICS, Permissions.READ_DESTINATION_METRICS],
                    type: 'and'
                }
            }
        },
        {
            title: 'Alerts',
            id: 'alerts',
            route: { name: 'alerts' },
            icon: AlertCircle,
            isActive: currentPath.includes('alerts'),
            accessRules: {
                permissions: {
                    values: [Permissions.READ_ALERTS, Permissions.WRITE_ALERTS],
                    type: 'or'
                }
            },
            unreadCount: unreadCount.value
        }
    ];
    return nav.filter((n) => hasAccess(authStore.user, n.accessRules || {}));
});

// Split into Entities and Debug sections
const entityNav = computed(() => navigation.value.filter((i) => i.id !== 'logs' && i.id !== 'alerts'));
const debugNav = computed(() => navigation.value.filter((i) => i.id === 'logs' || i.id === 'alerts'));
const { state } = useSidebar();
const isCollapsed = computed(() => state.value === 'collapsed');
</script>

<template>
    <SidebarGroup>
        <SidebarGroupLabel>Entities</SidebarGroupLabel>
        <SidebarMenu>
            <!-- Skeleton loading state -->
            <template v-if="isNavigationLoading">
                <SidebarMenuSkeleton v-for="n in 4" :key="n" :show-icon="true" :show-text="!isCollapsed" />
            </template>
            <!-- Actual navigation items -->
            <template v-else v-for="item in entityNav" :key="item.id">
                <SidebarMenuItem v-if="!item.items">
                    <SidebarMenuButton as-child :tooltip="item.title" :isActive="item.isActive">
                        <RouterLink :to="item.route" class="flex items-center w-full">
                            <component :is="item.icon" class="size-4" />
                            <span class="ml-2">{{ item.title }}</span>
                            <Badge variant="default" class="ml-2" v-if="item.beta">BETA</Badge>
                            <Badge variant="destructive" class="ml-auto" v-if="item.unreadCount">{{ item.unreadCount }}</Badge>
                        </RouterLink>
                    </SidebarMenuButton>
                </SidebarMenuItem>
                <template v-else>
                    <!-- collapsed: show only sub-items as top-level icons -->
                    <template v-if="isCollapsed">
                        <template v-for="sub in item.items" :key="sub.title">
                            <SidebarMenuItem>
                                <SidebarMenuButton as-child :tooltip="sub.title" :isActive="sub.isActive">
                                    <RouterLink :to="sub.url" class="relative">
                                        <component :is="sub.icon" class="size-4" />
                                        <span v-if="sub.isActive" class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-accent" />
                                    </RouterLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </template>
                    </template>
                    <!-- expanded: parent + collapsible sub-menu -->
                    <template v-else>
                        <Collapsible as-child :default-open="item.isActive" class="group/collapsible">
                            <SidebarMenuItem>
                                <CollapsibleTrigger as-child>
                                    <SidebarMenuButton :tooltip="item.title" :isActive="item.isActive">
                                        <component :is="item.icon" class="size-4" />
                                        <span class="ml-2">{{ item.title }}</span>
                                        <Badge v-if="item.beta" variant="default" class="ml-2">BETA</Badge>
                                        <ChevronRight
                                            class="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        <template v-for="sub in item.items" :key="sub.title">
                                            <SidebarMenuSubItem>
                                                <SidebarMenuSubButton as-child :isActive="sub.isActive">
                                                    <RouterLink :to="sub.url" class="flex items-center">
                                                        <component :is="sub.icon" class="mr-2 size-4" />
                                                        {{ sub.title }}
                                                    </RouterLink>
                                                </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                        </template>
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>
                    </template>
                </template>
            </template>
        </SidebarMenu>
    </SidebarGroup>
    <SidebarGroup>
        <SidebarGroupLabel>Debug</SidebarGroupLabel>
        <SidebarMenu>
            <!-- Skeleton loading state -->
            <template v-if="isNavigationLoading">
                <SidebarMenuSkeleton v-for="n in 2" :key="n" :show-icon="true" :show-text="!isCollapsed" />
            </template>
            <!-- Actual navigation items -->
            <template v-else v-for="item in debugNav" :key="item.id">
                <SidebarMenuItem>
                    <SidebarMenuButton as-child :tooltip="item.title" :isActive="item.isActive">
                        <RouterLink :to="item.route" class="flex items-center w-full relative">
                            <component :is="item.icon" class="size-4" />
                            <span class="ml-2">{{ item.title }}</span>
                            <!-- Red dot indicator when collapsed and has unread alerts -->
                            <span
                                v-if="isCollapsed && item.id === 'alerts' && item.unreadCount"
                                class="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-destructive" />
                            <!-- Badge when expanded -->
                            <Badge variant="destructive" class="ml-auto" v-if="!isCollapsed && item.unreadCount">{{ item.unreadCount }}</Badge>
                        </RouterLink>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </template>
        </SidebarMenu>
    </SidebarGroup>
</template>
